import React, { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ExternalLink,
  Shield
} from "lucide-react";
import Layout from "../components/layout/Layout";
import { useBooking } from "../context/BookingContext";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { usePayment } from "../context/PaymentContext";
import { useExpert } from "../hooks/useExperts";

export default function BookingPage() {
  const { id } = useParams();
  const { expert, loading: expertLoading } = useExpert(id);
  const { getAvailableSlots, hasAvailableSlots } = useBooking();
  const { user } = useAuth();
  const { calculateCommission } = useSubscription();
  const { createPayment, paymentLoading, paymentError } = usePayment();
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [topic, setTopic] = useState("");
  const [step, setStep] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const breakdown = expert ? calculateCommission(expert.hourlyRate) : null;

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayOfMonth = (y, m) => {
    const d = new Date(y, m, 1).getDay();
    return d === 0 ? 6 : d - 1;
  };

  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();
  const totalDays = daysInMonth(year, month);
  const startDay = firstDayOfMonth(year, month);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const calendarDays = useMemo(() => {
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    return cells;
  }, [startDay, totalDays]);

  const expertId = expert?.id;
  const availableDaysSet = useMemo(() => {
    const set = new Set();
    if (!expertId) return set;
    for (let d = 1; d <= totalDays; d++) {
      const dateObj = new Date(year, month, d);
      if (dateObj < today) continue;
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      if (hasAvailableSlots(expertId, dateStr)) {
        set.add(d);
      }
    }
    return set;
  }, [year, month, totalDays, expertId, hasAvailableSlots, today]);

  const slots = selectedDate && expertId
    ? getAvailableSlots(expertId, selectedDate)
    : [];

  if (expertLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!expert) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-medium text-white mb-4">
              Эксперт не найден
            </h1>
            <Link
              to="/experts"
              className="text-primary-400 hover:text-primary-300 text-lg font-extralight"
            >
              Вернуться к каталогу
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handleDateClick = (day) => {
    if (!day) return;
    const dateObj = new Date(year, month, day);
    if (dateObj < today) return;
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
  };

  const handlePayWithYookassa = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    setProcessing(true);
    setLocalError(null);

    try {
      const returnUrl = `${window.location.origin}/payment/result`;

      const result = await createPayment({
        expertId: expert.id,
        clientId: user.id,
        amountTotal: breakdown.clientPays,
        amountExpert: breakdown.expertEarnings,
        amountPlatform: breakdown.platformFee,
        commissionPercent: breakdown.commissionPercent,
        description: `Консультация с ${expert.name} — ${new Date(selectedDate).toLocaleDateString("ru-RU")} в ${selectedSlot}`,
        returnUrl: `${returnUrl}?transaction_id=${encodeURIComponent("PLACEHOLDER")}`,
        expertName: expert.name,
        clientName: user.name,
        date: selectedDate,
        time: selectedSlot,
        topic,
      });

      if (result.confirmationUrl) {
        // Update return URL with actual transaction ID
        const finalReturnUrl = `${returnUrl}?transaction_id=${result.transactionId}`;

        // For YooKassa, the confirmationUrl already includes the return_url
        // We redirect the user to YooKassa payment page
        window.location.href = result.confirmationUrl;
      } else {
        setLocalError("Не удалось получить ссылку на оплату. Попробуйте позже.");
        setProcessing(false);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setLocalError(err.message || "Ошибка создания платежа");
      setProcessing(false);
    }
  };

  const weekDays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
  const monthNames = [
    "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
  ];

  const errorMsg = localError || paymentError;

  return (
    <Layout>
      <div id="booking_page" className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to={`/experts/${expert.id}`}
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-lg font-extralight">
              Назад к профилю {expert.name}
            </span>
          </Link>

          <div className="flex items-center gap-4 mb-8 p-5 rounded-md bg-neutral-900/50 border border-neutral-800">
            <img
              src={expert.avatar}
              alt={expert.name}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div className="flex-1">
              <h2 className="text-xl font-medium text-white">{expert.name}</h2>
              <p className="text-lg font-extralight text-neutral-400">
                {expert.title}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-medium text-white">
                {breakdown.clientPays.toLocaleString("ru-RU")} ₽
              </p>
              <p className="text-lg font-extralight text-neutral-500">
                за 1 час
              </p>
            </div>
          </div>

          <div className="flex gap-4 mb-8">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`flex-1 flex items-center gap-3 p-3 rounded-md border ${
                  step >= s
                    ? "border-primary-600/50 bg-primary-950/20"
                    : "border-neutral-800 bg-neutral-900/30"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-lg font-medium ${
                    step >= s
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-800 text-neutral-500"
                  }`}
                >
                  {step > s ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    s
                  )}
                </div>
                <span
                  className={`text-lg font-extralight ${
                    step >= s ? "text-white" : "text-neutral-500"
                  }`}
                >
                  {s === 1 ? "Дата" : s === 2 ? "Время" : "Оплата"}
                </span>
              </div>
            ))}
          </div>

          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800"
            >
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => {
                    const prev = new Date(year, month - 1, 1);
                    setCalendarMonth(prev);
                  }}
                  className="p-2 rounded-md hover:bg-neutral-800 text-neutral-400 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="text-xl font-medium text-white">
                  {monthNames[month]} {year}
                </h3>
                <button
                  onClick={() => {
                    const next = new Date(year, month + 1, 1);
                    setCalendarMonth(next);
                  }}
                  className="p-2 rounded-md hover:bg-neutral-800 text-neutral-400 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((d) => (
                  <div
                    key={d}
                    className="text-center text-lg font-extralight text-neutral-500 py-2"
                  >
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, i) => {
                  if (day === null) {
                    return <div key={`empty-${i}`} />;
                  }
                  const dateObj = new Date(year, month, day);
                  const isPast = dateObj < today;
                  const hasSlots = !isPast && availableDaysSet.has(day);
                  const isDisabled = isPast || !hasSlots;
                  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const isSelected = selectedDate === dateStr;

                  return (
                    <button
                      key={day}
                      onClick={() => !isDisabled && handleDateClick(day)}
                      disabled={isDisabled}
                      className={`aspect-square flex items-center justify-center rounded-md text-lg font-extralight transition-colors ${
                        isDisabled
                          ? "text-neutral-700 cursor-not-allowed"
                          : isSelected
                            ? "bg-primary-600 text-white"
                            : "text-neutral-300 hover:bg-neutral-800"
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {selectedDate && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setStep(2)}
                    className="px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                  >
                    Выбрать время
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800"
            >
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-lg font-extralight">Назад</span>
                </button>
                <h3 className="text-xl font-medium text-white">
                  {new Date(selectedDate).toLocaleDateString("ru-RU", {
                    weekday: "long",
                    day: "numeric",
                    month: "long"
                  })}
                </h3>
                <div />
              </div>

              {slots.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                  <p className="text-lg font-extralight text-neutral-400">
                    На этот день эксперт не назначил свободных слотов
                  </p>
                  <button
                    onClick={() => setStep(1)}
                    className="mt-4 text-primary-400 hover:text-primary-300 text-lg font-extralight"
                  >
                    Выбрать другую дату
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {slots.map((slot) => (
                      <button
                        key={slot.time}
                        onClick={() =>
                          slot.available && setSelectedSlot(slot.time)
                        }
                        disabled={!slot.available}
                        className={`py-3 rounded-md text-lg font-extralight transition-colors ${
                          !slot.available
                            ? "bg-neutral-800/50 text-neutral-600 cursor-not-allowed line-through"
                            : selectedSlot === slot.time
                              ? "bg-primary-600 text-white"
                              : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>

                  {selectedSlot && (
                    <div className="mt-6">
                      <label className="block text-lg font-extralight text-neutral-400 mb-2">
                        Тема консультации (необязательно)
                      </label>
                      <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="О чём хотите поговорить?"
                        className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                      <div className="mt-6 flex justify-end">
                        <button
                          onClick={() => setStep(3)}
                          className="px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                        >
                          Перейти к оплате
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800"
            >
              <button
                onClick={() => setStep(2)}
                className="flex items-center gap-2 text-neutral-400 hover:text-white mb-6 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="text-lg font-extralight">Назад</span>
              </button>

              <h3 className="text-2xl font-medium text-white mb-6">
                Подтверждение бронирования
              </h3>

              <div className="space-y-4 mb-8">
                <div className="flex justify-between p-4 rounded-md bg-neutral-800/50">
                  <span className="text-lg font-extralight text-neutral-400">
                    Эксперт
                  </span>
                  <span className="text-lg font-medium text-white">
                    {expert.name}
                  </span>
                </div>
                <div className="flex justify-between p-4 rounded-md bg-neutral-800/50">
                  <span className="text-lg font-extralight text-neutral-400">
                    Дата
                  </span>
                  <span className="text-lg font-medium text-white">
                    {new Date(selectedDate).toLocaleDateString("ru-RU", {
                      weekday: "long",
                      day: "numeric",
                      month: "long"
                    })}
                  </span>
                </div>
                <div className="flex justify-between p-4 rounded-md bg-neutral-800/50">
                  <span className="text-lg font-extralight text-neutral-400">
                    Время
                  </span>
                  <span className="text-lg font-medium text-white">
                    {selectedSlot}
                  </span>
                </div>
                {topic && (
                  <div className="flex justify-between p-4 rounded-md bg-neutral-800/50">
                    <span className="text-lg font-extralight text-neutral-400">
                      Тема
                    </span>
                    <span className="text-lg font-medium text-white">
                      {topic}
                    </span>
                  </div>
                )}
                <div className="flex justify-between p-4 rounded-md bg-primary-950/30 border border-primary-600/30">
                  <span className="text-xl font-medium text-white">К оплате</span>
                  <span className="text-xl font-medium text-white">
                    {breakdown.clientPays.toLocaleString("ru-RU")} ₽
                  </span>
                </div>
              </div>

              {errorMsg && (
                <div className="mb-4 p-4 rounded-md bg-red-500/10 border border-red-500/30">
                  <p className="text-lg font-extralight text-red-400">
                    {errorMsg}
                  </p>
                </div>
              )}

              <button
                onClick={handlePayWithYookassa}
                disabled={processing || paymentLoading}
                className="w-full py-4 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {processing || paymentLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <CreditCard className="w-5 h-5" />
                )}
                {processing || paymentLoading
                  ? "Создание платежа..."
                  : "Оплатить через ЮKassa"}
              </button>

              <div className="flex items-center justify-center gap-2 mt-4">
                <Shield className="w-4 h-4 text-neutral-500" />
                <p className="text-lg font-extralight text-neutral-500">
                  Безопасная оплата через ЮKassa. Данные карты защищены.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
}
